/**
 * The diet plan's rings, in the app's progress-ring language: a green arc over a light track, from 12 o'clock.
 * ProgressRing draws one value (a day in the tracker, a macro's share, the week's average); MealPlate is the day split
 * into its meals, each arc as long as the meal's calories, green once logged and light green for the next one.
 * Both draw in their own viewBox and take their size from CSS, so one ring serves phones and desktop.
 */

const clamp = (value) => Math.min(Math.max(value || 0, 0), 1);

// `track`: "tint" (green mixed into the card, for macros and averages) or "gray" (a day's tracker, a stand-in)
export const ProgressRing = ({ value, size, stroke, track = 'tint', className = '', children }) => {
  const center = size / 2;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const fraction = clamp(value);
  return (
    <span className={`mt-dp-ring ${className}`} style={{ '--mt-dp-ring-size': `${size}px` }}>
      <svg viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle className={`mt-dp-ring__track mt-dp-ring__track--${track}`} cx={center} cy={center} r={radius} fill="none" strokeWidth={stroke} />
        {fraction > 0 && (
          <circle
            className="mt-dp-ring__arc"
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            // A sliver still shows as a dot
            strokeDashoffset={circumference * (1 - Math.max(fraction, 0.02))}
            style={{ '--mt-ring-full': circumference }}
          />
        )}
      </svg>
      {children}
    </span>
  );
};

// The space between two meals' arcs, as a share of the ring
const GAP = 0.012;

// `segments`: one per meal, [{ key, kcal, state: 'logged' | 'next' | 'idle', icon, isFree }]; icons only on the big
// plate, where the free meal's star keeps its green until the meal is logged
export const MealPlate = ({ segments, size, stroke, gap = GAP, className = '', children }) => {
  const center = size / 2;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((sum, segment) => sum + (segment.kcal || 0), 0);

  let start = 0;
  const arcs = [];
  const marks = [];
  segments.forEach((segment) => {
    const length = total ? (segment.kcal || 0) / total : 0;
    if (length > 0) {
      arcs.push(
        <circle
          key={segment.key}
          className={`mt-dp-plate__arc is-${segment.state}`}
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={`${Math.max(length - gap, 0) * circumference} ${circumference}`}
          strokeDashoffset={-(start + gap / 2) * circumference}
        />
      );
      if (segment.icon) {
        // The middle of the meal's arc; the svg turns -90° so the ring starts at 12 o'clock, the marks don't
        const angle = (start + length / 2) * 2 * Math.PI - Math.PI / 2;
        marks.push(
          <span
            key={segment.key}
            className={`mt-dp-plate__mark is-${segment.state}${segment.isFree ? ' is-free' : ''}`}
            style={{ left: `${((center + radius * Math.cos(angle)) / size) * 100}%`, top: `${((center + radius * Math.sin(angle)) / size) * 100}%` }}
          >
            {segment.icon}
          </span>
        );
      }
    }
    start += length;
  });

  return (
    <span className={`mt-dp-plate ${className}`} style={{ '--mt-dp-ring-size': `${size}px` }}>
      <svg viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        {arcs.length > 0 ? arcs : <circle className="mt-dp-plate__arc is-idle" cx={center} cy={center} r={radius} fill="none" strokeWidth={stroke} />}
      </svg>
      {marks.length > 0 && (
        <span className="mt-dp-plate__marks" aria-hidden="true">
          {marks}
        </span>
      )}
      {children}
    </span>
  );
};
